import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Mail, Settings } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { useInstructorAuth } from '../../hooks/useInstructorAuth';

const InstructorUnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();
  const { instructor, permissions, logout } = useInstructorAuth();

  const handleGoBack = () => {
    navigate('/instructor/dashboard');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/instructor/login');
  };

  const handleContactAdmin = () => {
    // Ouvrir l'email ou rediriger vers une page de contact
    window.location.href = 'mailto:admin@ecatheba.com?subject=Demande de permissions instructeur';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full shadow-xl border-0">
        <CardHeader className="text-center pb-6">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-red-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            Accès Non Autorisé
          </CardTitle>
          <CardDescription className="text-gray-600 text-lg">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Informations sur l'utilisateur */}
          {instructor && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">
                Informations de votre compte
              </h3>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-medium">Nom:</span> {instructor.name}
                </p>
                <p>
                  <span className="font-medium">Email:</span> {instructor.email}
                </p>
                <p>
                  <span className="font-medium">Statut:</span>{' '}
                  <span className={instructor.is_active ? 'text-green-600' : 'text-red-600'}>
                    {instructor.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </p>
              </div>
            </div>
          )}

          {/* Permissions actuelles */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <Settings className="h-4 w-4 mr-2" />
              Vos permissions actuelles
            </h3>
            {permissions && permissions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {permissions.map((permission, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {permission.replace('_', ' ').toUpperCase()}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-sm">
                Aucune permission spécifique accordée
              </p>
            )}
          </div>

          {/* Que faire maintenant */}
          <div className="bg-yellow-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">
              Que faire maintenant ?
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="font-medium mr-2">1.</span>
                Contactez l'administrateur pour demander les permissions nécessaires
              </li>
              <li className="flex items-start">
                <span className="font-medium mr-2">2.</span>
                Vérifiez que votre compte instructeur est bien activé
              </li>
              <li className="flex items-start">
                <span className="font-medium mr-2">3.</span>
                Retournez au tableau de bord pour accéder aux fonctionnalités autorisées
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              onClick={handleGoBack}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour au tableau de bord
            </Button>
            
            <Button
              onClick={handleContactAdmin}
              variant="outline"
              className="flex-1 border-orange-200 text-orange-700 hover:bg-orange-50"
            >
              <Mail className="h-4 w-4 mr-2" />
              Contacter l'admin
            </Button>
          </div>

          {/* Déconnexion */}
          <div className="border-t pt-4">
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full text-gray-600 hover:text-gray-800"
            >
              Se déconnecter
            </Button>
          </div>

          {/* Informations de contact */}
          <div className="text-center text-xs text-gray-500 pt-4 border-t">
            <p>
              Pour toute question, contactez le support à{' '}
              <a
                href="mailto:support@ecatheba.com"
                className="text-blue-600 hover:underline"
              >
                support@ecatheba.com
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InstructorUnauthorizedPage;
